import React, { RefObject, useMemo } from 'react';
import {
  selectVerticalProfilePlotData,
  SingleVerticalProfilePlotState,
} from 'ducks/plot/vertical-profile';
import { FullState } from 'main/reducers';
import { useSelector } from 'react-redux';
import ErrorNotice from 'components/base/form/errornotice/errornotice';
import Loading from 'components/base/loading/loading';
import { StoredVerticalProfilePlotWithArea } from 'ducks/stored-plot/detail';
import {
  computeVerticalProfilePlotItemDateRange,
  VerticalProfilePlot,
} from './VerticalProfilePlot';
import { formatDatetimeForDisplay } from 'util/dates';
import { PageOrientations } from 'components/modules/exportpanel/exportpanelconstants';
import { StoredVerticalProfilePlotItem } from 'util/backendapi/types/Model';

interface Props {
  storedPlot: StoredVerticalProfilePlotWithArea;
  buttonsPortal?: RefObject<HTMLElement>;
}

export function VerticalProfileStoredPlot(props: Props) {
  const { storedPlot } = props;

  const plotState = useSelector((state: FullState) =>
    selectVerticalProfilePlotData(state, storedPlot.id)
  );

  const errorMessage = plotState?.errorMessage ?? null;
  const isLoading = plotState?.isLoading ?? false;

  if (errorMessage) {
    return <ErrorNotice>{errorMessage}</ErrorNotice>;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (
    !plotState?.plotItemReadings ||
    Object.values(plotState?.plotItemReadings).length !==
      storedPlot.stored_vertical_profile_plot_items.length
  ) {
    return null;
  }

  return (
    <div className="page-multi-plot page-multi-plot-vertical-profile">
      {storedPlot.stored_vertical_profile_plot_items.map((item) => (
        <VerticalProfileStoredPlotItem
          storedPlot={storedPlot}
          plotState={plotState}
          item={item}
          key={item.id}
        />
      ))}
    </div>
  );
}

interface VerticalProfileStoredPlotItemProps {
  storedPlot: StoredVerticalProfilePlotWithArea;
  plotState: SingleVerticalProfilePlotState;
  item: StoredVerticalProfilePlotItem;
}

function VerticalProfileStoredPlotItem(
  props: VerticalProfileStoredPlotItemProps
) {
  const { storedPlot, plotState, item } = props;

  const plotItemReadings = plotState?.plotItemReadings?.[item.id] ?? null;

  const { minDatetime, maxDatetime } = useMemo(() => {
    return computeVerticalProfilePlotItemDateRange(
      storedPlot,
      plotItemReadings
    );
  }, [plotItemReadings, storedPlot]);

  const timeZone = storedPlot.area.time_zone.name;

  return (
    <div className="plot-item plot-item-vertical-profile">
      <div className="plot-item-detail">
        <h3 className="plot-item-header">{item.name}</h3>
        <p>{item.description}</p>
        <p data-testid="plot-date-range">
          {formatDatetimeForDisplay(minDatetime, timeZone)} -{' '}
          {formatDatetimeForDisplay(maxDatetime, timeZone)}
        </p>
      </div>
      {plotItemReadings ? (
        <VerticalProfilePlot
          storedPlot={storedPlot}
          storedVerticalProfilePlotItem={item}
          plotReadings={plotItemReadings}
          orientation={PageOrientations.LANDSCAPE}
        />
      ) : null}
    </div>
  );
}
